/** The dot on the top-bar pill (plan §9.2, §9.4): a turn is running, or a change waits for the user's OK. */
export type TAssistantDot = 'working' | 'pending' | null;

/** A proposal the user has not applied or discarded yet (keyed by proposal id). */
export interface IAssistantPendingProposal {
  rows: number;
  /** ISO instant after which the proposal can no longer be applied. */
  expiresAt: string;
  /** `r{rid}-b{bid}` of the store and branch it was prepared for. */
  scopeKey: string;
}

export type TAssistantPendingProposals = Record<
  string,
  IAssistantPendingProposal
>;

/**
 * Ids of the pending proposals of `scopeKey` (the current store and branch;
 * null outside a store) still open at `nowMs`. An unreadable expiry counts
 * as expired.
 */
export function livePendingProposalIds(
  pending: TAssistantPendingProposals,
  nowMs: number,
  scopeKey: string | null
): string[] {
  return Object.keys(pending).filter((id) => {
    const proposal = pending[id];
    const expiresAtMs = Date.parse(proposal.expiresAt);
    return (
      proposal.scopeKey === scopeKey &&
      Number.isFinite(expiresAtMs) &&
      expiresAtMs > nowMs
    );
  });
}

/**
 * `working` wins over `pending`; expired proposals and those of another
 * store or branch never show the dot.
 */
export function assistantDot(
  busy: boolean,
  pending: TAssistantPendingProposals,
  nowMs: number,
  scopeKey: string | null
): TAssistantDot {
  if (busy) return 'working';
  return livePendingProposalIds(pending, nowMs, scopeKey).length > 0
    ? 'pending'
    : null;
}
